"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
// ✅ CRITICAL: Import directly, not from @/ui/primitives barrel
// The barrel exports ALL primitives (DataTable, Pagination, Tabs, etc.) which aren't needed for error boundary
import { BaseCard } from "@/ui/primitives/BaseCard";
import { BaseButton } from "@/ui/primitives/BaseButton";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error UI component that uses React Router navigation
 * This is a functional component so it can use hooks
 */
function ErrorUI({
  error,
  errorInfo,
  onReset,
}: {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
}) {
  const navigate = useNavigate();

  const handleGoHome = () => {
    // Navigate to /home - let the app's routing/auth system handle authentication
    // Token persistence is handled by TokenStorage in localStorage
    navigate("/home", { replace: true });
  };

  const isDev =
    typeof window !== "undefined" &&
    ((import.meta as any)?.env?.DEV ||
      (import.meta as any)?.env?.MODE === "development");

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center p-4">
      <BaseCard className="max-w-2xl w-full">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-16 h-16 text-status-error" />
          </div>

          <h1 className="text-[24px] font-bold text-primary mb-2">
            Something went wrong
          </h1>

          <p className="text-[15px] text-secondary mb-6">
            We're sorry, but something unexpected happened. Please try again or
            contact support if the problem persists.
          </p>

          {isDev && error && (
            <div className="mb-6 p-4 bg-status-error-bg border border-status-error rounded-lg text-left">
              <p className="text-sm font-semibold text-status-error mb-2">
                Error Details (Development Only):
              </p>
              <pre className="text-xs text-status-error overflow-auto">
                {error.toString()}
                {errorInfo && (
                  <>
                    {"\n\n"}
                    {errorInfo.componentStack}
                  </>
                )}
              </pre>
            </div>
          )}

          <div className="flex gap-3 justify-center">
            <BaseButton
              onClick={onReset}
              variant="primary"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </BaseButton>

            <BaseButton
              onClick={handleGoHome}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Home className="w-4 h-4" />
              Go Home
            </BaseButton>
          </div>
        </div>
      </BaseCard>
    </div>
  );
}

/**
 * ErrorBoundary - Catches React errors and displays a fallback UI
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // TODO: Log to error tracking service (e.g., Sentry)
    // logErrorToService(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use functional component for error UI so it can use React Router hooks
      return (
        <ErrorUI
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
